import { useEffect, useRef, useState } from 'react';
import React from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { deleteCertification, getAllCertifications, uploadFile } from '../../services/certifications';
import { FiSearch } from 'react-icons/fi';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import './certifications.css';
import { getUserInfo, getUserRolePermissions } from '../../services/userServicer';
import { useSearchQuery } from 'src/hook/useSearchQuery';
import LoadingScreen from '../../Loading/LoadingScreen';
import LoadingButton from '@/components/Buttons/LoadingButton';
interface Certification {
    id: number;
    name: string;
    issuer: string;
    date: string;
    /** API image path segment */
    src?: string;
}

interface ListCertificationsProps {
    certifications: Certification[];
}

/** Role API: one object per `page` with flags on the same object (no nested `.permissions`). */
const normalizeCertificationsPage = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const matchesCertificationsPage = (value: unknown) => {
  const n = normalizeCertificationsPage(value);
  return (
    n === 'certifications' ||
    n === 'sup-certifications' ||
    n === 'sup_certifications' ||
    n === 'certificationssup'
  );
};

const permissionToBool = (value: unknown): boolean => {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }
  return Boolean(value);
};

export default function CertificationsListSup() {
    const [certifications, setCertifications] = useState<Certification[]>([]);
    const [pageNo, setPageNo] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const perPage = 9;
    const {t}=useTranslation();

    const [showModal, setShowModal] = useState(false);
    const [nameCertification, setNameCertification] = useState('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [date, setDate] = useState('');
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedCertification, setSelectedCertification] = useState<Certification | null>(null);
    const [permissions, setPermissions] = useState<any>(null);
    const [notAllowed, setNotAllowed] = useState(false);
    const [permissionsChecked, setPermissionsChecked] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const didSkipFirstSearch = useRef<boolean>(false);

    // Use the search query hook
    const { searchQuery, searchResultType, clearSearchQuery } = useSearchQuery();


    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
       setNameCertification(e.target.value)
    };

    const handleSubmit = async () => {
        // Validate file upload
        if (!uploadedFile) {
            toast.error("Please upload a file.");
            return; // Don't close modal or clear form
        }

        // Validate name
        if (!nameCertification.trim()) {
            toast.error("Please enter a certification name.");
            return; // Don't close modal or clear form
        }

        try {
            let formData = new FormData();
            formData.append('files', uploadedFile);
            formData.append('name', nameCertification);
            const response = await uploadFile(formData);
            const responseData = await response.json();
            
            if (response.ok) {
                toast.success(responseData.message);
                // Only clear and close on success
                setNameCertification("");
                setUploadedFile(null);
                handleClose();
                getCertifications();
            } else {
                toast.error(responseData.message);
                // Don't close modal on error - let user fix and retry
            }
        } catch (error: any) {
            toast.error("Error creating certification:", error);
            // Don't close modal on error - let user fix and retry
        }
    };

 const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
        const file = files[0];

        if (!file.type.startsWith("image/")) {
            toast.error("Only image files are allowed.");
            return;
        }

        setUploadedFile(file);
    }
};


    const getCertifications = async (searchQuery: string = '', pageOverride?: number) => {
        setLoading(true);
        const apiPage = pageOverride !== undefined ? pageOverride : pageNo;
        try {
            const response :any= await getAllCertifications(searchQuery, perPage, apiPage);

            if(response.success){
                setCertifications(response.data);
                setTotalPages(response.totalPages); 
            }
            else{
                toast.error(response.message);
                console.error('Error fetching certifications:', response.message);
            }
        } catch (error) {
            toast.error('Error fetching certifications');
            console.error('Error fetching certifications:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const fetchPermissions = async () => {
          setLoading(true);
          let authenticated = false;
          try {
            const user = await getUserInfo();
            if (!user?.id) {
              setPermissions(null);
              setNotAllowed(true);
              return;
            }

            authenticated = true;
            /** View is not gated by role `show` for this page; CRUD still uses API flags when present. */
            setNotAllowed(false);

            const response = await getUserRolePermissions(user.id);

            if (
              response?.success &&
              response?.data?.permissions &&
              Array.isArray(response.data.permissions)
            ) {
              const certPerm = response.data.permissions.find((perm: { page?: string }) =>
                matchesCertificationsPage(perm?.page)
              );

              if (certPerm) {
                setPermissions(certPerm);
              } else {
                setPermissions(null);
              }
            } else {
              setPermissions(null);
            }
          } catch {
            setPermissions(null);
            setNotAllowed(!authenticated);
          } finally {
            setPermissionsChecked(true);
            setLoading(false);
            if (authenticated) {
              didSkipFirstSearch.current = true;
            }
          }
        };

        fetchPermissions();
      }, []);

      // Handle search query from global search
      useEffect(() => {
        if (!permissionsChecked) return;
        if (searchQuery && searchResultType === 'certifications') {
          setSearchTerm(searchQuery);
          setPageNo(0);
          void getCertifications(searchQuery, 0);
          clearSearchQuery();
        }
      }, [searchQuery, searchResultType, clearSearchQuery, permissionsChecked, permissions]);

      // Handle local search with debouncing
      useEffect(() => {
        if (!permissionsChecked) return;
        if (didSkipFirstSearch.current) {
          didSkipFirstSearch.current = false;
          return;
        }

        const handler = setTimeout(() => {
          // Reset to first page when search term changes
          setPageNo(0);
          const q = (searchTerm || '').trim();
          if (q.length === 0) {
            void getCertifications('', 0);
          } else {
            void getCertifications(q, 0);
          }
        }, 1000);

        return () => clearTimeout(handler);
      }, [searchTerm, permissionsChecked, permissions]);

      // Refetch when page changes
      useEffect(() => {
        if (!permissionsChecked) return;
        getCertifications((searchTerm || '').trim());
      }, [pageNo, permissionsChecked, permissions]);
      
   
    const handleImageClick = (imageSrc: string) => {
        setSelectedImage(imageSrc);
        // Open image in new tab
        window.open(imageSrc, '_blank');
    };

    const handleSignatureSubmit = () => {
        if (canvasRef.current) {
            const dataUrl = canvasRef.current.toDataURL();
            //console.log('Signature Data URL:', dataUrl);
            //console.log('Date:', date);
            setShowModal(false);
        }
    };

    const handleDelete = (id: number) => {
        setSelectedCertification(certifications.find(cert => cert.id === id) || null);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (selectedCertification) {
            //console.log(`Confirmed delete for certification with id: ${selectedCertification.id}`);
    try{
    const response = await deleteCertification(selectedCertification.id);
    if(response.success){
        toast.success(response.message);
        getCertifications();
            }
            else{
            toast.error(response.message);
            }
    }catch(error:any){
            toast.error(error.message);
    }
            
            // Implement actual delete logic here
            setShowDeleteModal(false);
        }
    };

    const handleUpdate = (id: number) => {
        setSelectedCertification(certifications.find(cert => cert.id === id) || null);
        setShowUpdateModal(true);
    };

    const confirmUpdate = async () => {
        if (selectedCertification) {
          try {
            let formData = new FormData();
      
            // Append the ID of the certification to be updated
            formData.append('id', selectedCertification.id.toString());
      
            // Append updated name only if provided
            if (nameCertification && nameCertification !== selectedCertification.name) {
              formData.append('name', nameCertification);
            } else {
              formData.append('name', selectedCertification.name); // Send existing name if not updated
            }
      
            // Append file only if a new file is uploaded
            if (uploadedFile) {
              formData.append('files', uploadedFile);
            }
      
            const response = await uploadFile(formData);
            const responseData = await response.json();
      
            if (response.ok) {
              toast.success(responseData.message);
            } else {
              toast.error(responseData.message);
            }
      
            // Clear form fields after update
            setNameCertification("");
            setUploadedFile(null);
            setShowUpdateModal(false);
            getCertifications();
          } catch (error: any) {
            toast.error("Error updating certification:", error.message);
          }
        }
      };
      

    const canCreate = permissionToBool(permissions?.create);
    const canEdit = permissionToBool(permissions?.edit);
    const canDelete = permissionToBool(permissions?.delete);
    const tr = (key: string) => String(t(`Certifications.${key}`));
    const totalPagesSafe = Math.max(totalPages || 1, 1);

    if (!permissionsChecked || loading) {
        return <LoadingScreen />;
    }

   if (notAllowed) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <div
          className="alert alert-danger text-center"
          style={{
            maxWidth: "500px",
            width: "100%",
            padding: "32px 24px",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            background: "#f8d7da",
            color: "#842029",
            fontSize: "1.2rem",
          }}
        >
          {t("You do not have permission to view this page.")}
        </div>
      </div>
    );
  }
      

    return (
        <div className="cert-sup-page">
       

            <div className="cert-sup-control-card">
                <div className="cert-sup-toolbar">
                    <div className="cert-sup-search-wrap">
                        <FiSearch className="cert-sup-search-icon" size={18} aria-hidden />
                        <input
                            type="search"
                            className="cert-sup-search-input"
                            placeholder={tr("search_placeholder")}
                            aria-label={tr("search_placeholder")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setPageNo(0);
                                    void getCertifications((searchTerm || '').trim(), 0);
                                }
                            }}
                        />
                    </div>
                    <div className="cert-sup-toolbar-actions">
                        {canCreate ? (
                            <button type="button" className="cert-sup-btn cert-sup-btn-primary" onClick={handleShow}>
                                <FaPlus size={15} aria-hidden />
                                <span>{tr("add_certification")}</span>
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="cert-sup-meta-bar">
                <span className="cert-sup-total">
                    {tr("total_on_page")}: <strong>{certifications.length}</strong>
                </span>
            </div>

            <div className="cert-sup-grid-panel">
                {certifications.length > 0 ? (
                    <div className="cert-sup-grid">
                        {certifications.map((card: Certification) => {
                            const imgSrc = card.src
                                ? `${process.env.REACT_APP_API_URL}${card.src}?public=true`
                                : '';
                            return (
                            <article className="cert-sup-card" key={card.id}>
                                <div className="cert-sup-card-header">
                                    <h2 className="cert-sup-card-title">{card.name}</h2>
                                    <div className="cert-sup-card-actions">
                                        {canEdit ? (
                                            <button
                                                type="button"
                                                className="cert-sup-icon-btn cert-sup-icon-btn--edit"
                                                onClick={() => handleUpdate(card.id)}
                                                aria-label={tr('edit_aria')}
                                                title={String(t('Certifications.update'))}
                                            >
                                                <FaEdit size={15} aria-hidden />
                                            </button>
                                        ) : null}
                                        {canDelete ? (
                                            <button
                                                type="button"
                                                className="cert-sup-icon-btn cert-sup-icon-btn--delete"
                                                onClick={() => handleDelete(card.id)}
                                                aria-label={tr('delete_aria')}
                                                title={String(t('Certifications.delete'))}
                                            >
                                                <FaTrash size={15} aria-hidden />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                {imgSrc ? (
                                <button
                                    type="button"
                                    className="cert-sup-card-media"
                                    onClick={() => handleImageClick(imgSrc)}
                                    aria-label={tr('open_image')}
                                >
                                    <img src={imgSrc} alt={card.name} />
                                </button>
                                ) : (
                                    <div className="cert-sup-card-media cert-sup-card-media--placeholder" role="img" aria-hidden />
                                )}
                            </article>
                            );
                        })}
                    </div>
                ) : (
                    <div className="cert-sup-empty" role="status">
                        {tr('no_certifications_found')}
                    </div>
                )}
            </div>

            {certifications.length > 0 ? (
                <nav className="cert-sup-pagination" aria-label={String(t('Governance_Page.page'))}>
                    <button type="button" className="cert-sup-page-btn" onClick={() => setPageNo(0)} disabled={pageNo === 0}>
                        «
                    </button>
                    <button
                        type="button"
                        className="cert-sup-page-btn"
                        onClick={() => setPageNo((prev) => Math.max(prev - 1, 0))}
                        disabled={pageNo === 0}
                    >
                        ‹ {t('institution.pagination_previous')}
                    </button>
                    <span className="cert-sup-page-current" aria-current="page">
                        {pageNo + 1}
                    </span>
                    <button
                        type="button"
                        className="cert-sup-page-btn"
                        onClick={() => setPageNo((prev) => Math.min(prev + 1, totalPagesSafe - 1))}
                        disabled={pageNo >= totalPagesSafe - 1}
                    >
                        {t('institution.pagination_next')} ›
                    </button>
                    <button
                        type="button"
                        className="cert-sup-page-btn"
                        onClick={() => setPageNo(totalPagesSafe - 1)}
                        disabled={pageNo >= totalPagesSafe - 1}
                    >
                        »
                    </button>
                    <span className="cert-sup-page-meta">
                        {t('Governance_Page.page')} {pageNo + 1} {t('Governance_Page.to')} {totalPagesSafe}
                    </span>
                </nav>
            ) : null}

            <Modal isOpen={showModal} toggle={handleClose} centered className="cert-sup-modal">
                <ModalHeader toggle={handleClose} className="cert-sup-modal-header">
                    {t('Certifications.create_Certification')}
                </ModalHeader>
                <ModalBody className="cert-sup-modal-body">
                    <div className="cert-sup-modal-field">
                        <label className="cert-sup-modal-label" htmlFor="cert-create-name">
                            {t('Certifications.label')}
                        </label>
                        <Input
                            id="cert-create-name"
                            type="text"
                            placeholder={t('Certifications.label') || ''}
                            onChange={handleInputChange}
                            className="cert-sup-modal-input"
                            autoComplete="off"
                        />
                    </div>

                    <div className="cert-sup-modal-field">
                        <span className="cert-sup-modal-label">{t('Certifications.modal_certificate_image')}</span>
                        <label className="cert-sup-file-picker" htmlFor="cert-create-file">
                            <span className="cert-sup-file-picker-trigger">{tr('choose_file')}</span>
                            <span className="cert-sup-file-picker-name">
                                {uploadedFile ? uploadedFile.name : tr('no_file_chosen')}
                            </span>
                            <input
                                id="cert-create-file"
                                type="file"
                                accept="image/*"
                                className="cert-sup-file-picker-input"
                                onChange={handleFileUpload}
                            />
                        </label>
                        <p className="cert-sup-file-hint">{tr('image_hint')}</p>
                    </div>
                </ModalBody>
                <ModalFooter className="cert-sup-modal-footer cert-sup-modal-footer--actions">
                    <button type="button" className="cert-sup-modal-btn cert-sup-modal-btn--outline" onClick={handleClose}>
                        {t('Certifications.cancel')}
                    </button>
                    <LoadingButton
                        onClick={handleSubmit}
                        disabled={!canCreate}
                        className="cert-sup-modal-btn cert-sup-modal-btn--primary"
                        variant="success"
                        loadingText={String(t('Saving...') || 'Saving...')}
                    >
                        {t('Certifications.save')}
                    </LoadingButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showDeleteModal} toggle={() => setShowDeleteModal(false)} centered className="cert-sup-modal cert-sup-modal--danger">
                <ModalHeader toggle={() => setShowDeleteModal(false)} className="cert-sup-modal-header">
                    {t('Certifications.confirm_delete')}
                </ModalHeader>
                <ModalBody className="cert-sup-modal-body">
                    <p className="cert-sup-modal-text">{t('Certifications.confirm_delete_message')}</p>
                </ModalBody>
                <ModalFooter className="cert-sup-modal-footer cert-sup-modal-footer--actions">
                    <button type="button" className="cert-sup-modal-btn cert-sup-modal-btn--outline" onClick={() => setShowDeleteModal(false)}>
                        {t('Certifications.cancel')}
                    </button>
                    <LoadingButton
                        onClick={confirmDelete}
                        className="cert-sup-modal-btn cert-sup-modal-btn--danger"
                        variant="danger"
                        loadingText={String(t('Deleting...') || 'Deleting...')}
                    >
                        {t('Certifications.delete')}
                    </LoadingButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showUpdateModal} toggle={() => setShowUpdateModal(false)} centered className="cert-sup-modal">
                <ModalHeader toggle={() => setShowUpdateModal(false)} className="cert-sup-modal-header">
                    {t('Certifications.update_Certification')}
                </ModalHeader>
                <ModalBody className="cert-sup-modal-body">
                    <div className="cert-sup-modal-field">
                        <label className="cert-sup-modal-label" htmlFor="cert-update-name">
                            {t('Certifications.label')}
                        </label>
                        <Input
                            id="cert-update-name"
                            type="text"
                            defaultValue={selectedCertification?.name || ''}
                            onChange={handleInputChange}
                            className="cert-sup-modal-input"
                            autoComplete="off"
                        />
                    </div>
                    <div className="cert-sup-modal-field">
                        <span className="cert-sup-modal-label">{t('Certifications.modal_certificate_image')}</span>
                        <label className="cert-sup-file-picker" htmlFor="cert-update-file">
                            <span className="cert-sup-file-picker-trigger">{tr('choose_file')}</span>
                            <span className="cert-sup-file-picker-name">
                                {uploadedFile ? uploadedFile.name : tr('no_file_chosen')}
                            </span>
                            <input
                                id="cert-update-file"
                                type="file"
                                accept="image/*"
                                className="cert-sup-file-picker-input"
                                onChange={(e) => {
                                    const files = e.target.files;
                                    if (files && files.length > 0) {
                                        const file = files[0];
                                        if (!file.type.startsWith('image/')) {
                                            toast.error('Only image files are allowed.');
                                            return;
                                        }
                                        setUploadedFile(file);
                                    }
                                }}
                            />
                        </label>
                        <p className="cert-sup-file-hint">{tr('image_hint')}</p>
                    </div>
                </ModalBody>
                <ModalFooter className="cert-sup-modal-footer cert-sup-modal-footer--actions">
                    <button type="button" className="cert-sup-modal-btn cert-sup-modal-btn--outline" onClick={() => setShowUpdateModal(false)}>
                        {t('Certifications.cancel')}
                    </button>
                    <LoadingButton
                        onClick={confirmUpdate}
                        className="cert-sup-modal-btn cert-sup-modal-btn--primary"
                        variant="success"
                        loadingText={String(t('Saving...') || 'Saving...')}
                    >
                        {t('Certifications.save')}
                    </LoadingButton>
                </ModalFooter>
            </Modal>
        </div>
    );
}
